import "core-js/features/array/at";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { forwardRef, Ref, useCallback, useEffect, useMemo } from "react";

import {
  LISBlock,
  LISCase,
  LISSpecimen,
  SpecimenProtocolOverride,
} from "pwapi";

import { LabPanelList, LabStainList } from "custom-models/frontLoader";

import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import {
  printPieceCountSpecimen,
  printSpecimen,
} from "app/actions/specimenActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useGrossingMadLib from "hooks/useGrossingMadLib";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import Waiting from "ui/components/waiting";
import { GrossingConditional } from "util/grossingMadLibs/conditionals";
import { grossingModifiers } from "util/grossingMadLibs/modifiers";
import { GrossingToken } from "util/grossingMadLibs/tokens";

const GrossMissing = ({
  focused = false,
  setCurrentToken,
}: {
  focused: boolean;
  setCurrentToken: Function;
}) => {
  return (
    <span
      className={classNames("Grossing__missing", {
        "Grossing__missing--focused": focused,
      })}
      onClick={() => setCurrentToken()}
    >
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    </span>
  );
};

const GrossFilled = ({
  value,
  setCurrentToken,
  focused,
}: {
  value: string;
  setCurrentToken: Function;
  focused: boolean;
}) => (
  <span
    className={classNames("Grossing__filled", {
      "Grossing__filled--focused": focused,
    })}
    onClick={() => setCurrentToken()}
  >
    {value || <span>&nbsp;&nbsp;&nbsp;</span>}
  </span>
);

const GrossDescription = forwardRef(
  (
    props: ReturnType<typeof useGrossingMadLib> & { specimen: LISSpecimen },
    ref: Ref<HTMLParagraphElement>
  ) => {
    const {
      setCurrentToken,
      currentToken,
      tokenSelections,
      modifierSelections,
      parts,
      specimen,
      labStainSelections,
      labPanelSelections,
    } = props;

    const labPanels = useAppSelector((state) => state.labPanels);
    const labStains = useAppSelector((state) => state.labStains);
    const stains = useAppSelector((state) => state.stains);

    const specialStainsStr = useMemo(() => {
      // For selected Lab Stains we just get the abbreviation of the Stain
      const labStainStrs = labStainSelections
        .map((id) => stains[labStains[id]?.stain_id]?.abbreviation)
        .filter((str) => !!str);

      // For selected Lab Panels we try to get the abbreviations of any Special Stains in the Panel.
      const labPanelStrs = labPanelSelections
        .map((id) => {
          const labPanel = labPanels[id];
          if (!labPanel) return null;
          const labPanelStainStrs = labPanel.lab_stain_ids
            .map((id) => stains[labStains[id]?.stain_id])
            .filter((stain) => ["SPEC", "PW-SPEC"].includes(stain?.category))
            .map((stain) => stain.abbreviation);

          return labPanelStainStrs.length ? labPanelStainStrs : "";
        })
        .flat()
        .filter((str) => !!str) as string[];

      const strs = labStainStrs.concat(labPanelStrs);

      if (!strs.length) return "";

      let str = "";
      if (strs.length === 1) {
        str = strs[0];
      } else if (strs.length === 2) {
        str = strs.join(" and ");
      } else {
        str = `${strs.slice(0, -1).join(", ")}, and ${strs[strs.length - 1]}`;
      }

      return ` Special stains: ${str}.`;
    }, [labPanels, labStains, stains, labStainSelections, labPanelSelections]);

    return (
      <p ref={ref}>
        {parts.map((part, i: number) => {
          const token = part as GrossingToken;

          if (token.token) {
            const tokenSelection = tokenSelections[token.token];

            if (tokenSelection?.value !== undefined) {
              return (
                <GrossFilled
                  key={`token-${token.token}-${i}`}
                  focused={currentToken === token.token}
                  setCurrentToken={() => setCurrentToken(token.token)}
                  value={
                    token.conditionalOutput
                      ? token.conditionalOutput(tokenSelection.value, {
                          modifierSelections,
                          tokenSelections,
                          specimen,
                        })
                      : tokenSelection.value
                  }
                />
              );
            }

            return (
              <GrossMissing
                key={`token-${token.token}-${i}`}
                focused={currentToken === token.token}
                setCurrentToken={() => setCurrentToken(token.token)}
              />
            );
          }

          const conditional = part as GrossingConditional;
          if (conditional instanceof Function) {
            return conditional({
              modifierSelections,
              tokenSelections,
              specimen,
            });
          }

          return <span key={`part-${i}`}>{part}</span>;
        })}
        {specialStainsStr}
      </p>
    );
  }
);

const GrossInputs = forwardRef(
  (
    props: ReturnType<typeof useGrossingMadLib> & { specimen: LISSpecimen },
    ref: Ref<HTMLInputElement>
  ) => {
    const {
      tokenSelections,
      hasTips,
      isDIFSelected,
      isSpread,
      isEyeWedge,
      isFalseMargins,
      currentToken,
      modifierSelections,
      selectPrevToken,
      selectNextToken,
      setTokenValue,
      specimen,
      tokenParts,
    } = props;

    let token = tokenParts.find((token) => token.token === currentToken);
    if (
      token &&
      token.token === "grossed" &&
      specimen.protocols?.includes("alopecia")
    ) {
      token = {
        ...token,
        options: ["horizontally bisected", "vertically bisected", ""],
      };
    }
    const tokenSelection = tokenSelections[currentToken || ""];

    const handleDefaultValue = useCallback(
      () =>
        currentToken &&
        setTokenValue(
          currentToken,
          token?.defaultCustomValue
            ? `${
                typeof token.defaultCustomValue === "function"
                  ? token.defaultCustomValue()
                  : token.defaultCustomValue
              }`
            : "",
          true
        ),
      [currentToken]
    );

    if (
      currentToken &&
      token &&
      token.options.length === 0 &&
      token.defaultCustomValue &&
      !tokenSelection.value
    ) {
      handleDefaultValue();
    }

    const cassetteToken =
      currentToken === "cassette_count" ||
      (typeof currentToken === "string" &&
        (currentToken.endsWith("cassette_choice") ||
          currentToken.endsWith("cassette_range") ||
          currentToken.startsWith("type_cassette_count")));

    // Limit options for Cassette Choice/Range Tokens based on cassette_count values
    const options: string[] = useMemo(() => {
      if (!token) return [];
      if (
        typeof currentToken === "string" &&
        currentToken.endsWith("cassette_choice")
      ) {
        if (tokenSelections.cassette_count?.value)
          return token.options.slice(
            0,
            parseInt(tokenSelections.cassette_count.value)
          );
        return token.options;
      }
      if (
        typeof currentToken === "string" &&
        currentToken.endsWith("cassette_range")
      ) {
        let start = 1,
          end = undefined;

        const startToken =
          tokenSelections[currentToken.replace("_range", "_choice")];
        if (startToken?.value) start = parseInt(startToken.value);

        if (tokenSelections.cassette_count?.value)
          end = parseInt(tokenSelections.cassette_count.value);

        return token.options.slice(start, end);
      }

      if (
        typeof currentToken === "string" &&
        currentToken.startsWith("type_cassette_count") &&
        tokenSelections.cassette_count?.value &&
        tokenSelections.type_count?.value
      ) {
        const cassetteCount = parseInt(tokenSelections.cassette_count.value);
        const cassettes = Array.from({ length: cassetteCount - 1 }, (_, i) =>
          (i + 2).toString()
        );
        const typeCount = parseInt(tokenSelections.type_count.value);
        const typeCassetteAboveOne = currentToken.match(
          /type_cassette_count_(\d+)/
        );

        let start = 0,
          end = cassetteCount - typeCount;

        if (typeCassetteAboveOne) {
          const number = parseInt(typeCassetteAboveOne[1]);
          const isLast = number === typeCount;
          const previousValue = parseInt(
            tokenSelections[`type_cassette_count_${number - 1}`]?.value ||
              tokenSelections.type_cassette_count?.value ||
              "0"
          );

          start = previousValue - 1;
          end = isLast ? cassetteCount : -(typeCount - number);
        }

        return cassettes.slice(start, end);
      }

      // Limit cassette_count options based on type_count. If multipleShaves/Excisions/Punches is selected, limit cassetes to more or equal than type_count.
      if (
        currentToken === "cassette_count" &&
        tokenSelections.type_count?.value &&
        (modifierSelections.multipleShaves ||
          modifierSelections.multipleExcisions ||
          modifierSelections.multiplePunches)
      ) {
        const start = parseInt(tokenSelections.type_count.value) - 2,
          end = 8;

        return token.options.slice(start, end);
      }

      if (currentToken === "cassette_count" && isDIFSelected) {
        return [];
      }

      return token.options;
    }, [currentToken, tokenSelections.cassette_count?.value]);

    const startingTypeOption =
      currentToken === "type_cassette_count" ? "1" : options[0];

    const lastStartingTypeOption = useMemo(() => {
      if (
        typeof currentToken === "string" &&
        currentToken.startsWith("type_cassette_count_")
      ) {
        const tokenNumber = parseInt(
          currentToken.replace("type_cassette_count_", "")
        );
        if (
          tokenNumber === parseInt(tokenSelections.type_count?.value || "0")
        ) {
          return (
            parseInt(
              tokenSelections[`type_cassette_count_${tokenNumber - 1}`]
                ?.value ?? "0"
            ) + 1
          );
        }
      }
      return null;
    }, [currentToken, tokenSelections]);

    const allowCustom =
      typeof token?.allowCustom === "function"
        ? token.allowCustom({
            selections: { modifierSelections, tokenSelections, specimen },
          })
        : token?.allowCustom;

    return (
      <div className="Grossing__token-inputs">
        <button onClick={() => selectPrevToken()} className="Selection__prev">
          &larr;
        </button>
        <div
          className="Grossing__token-input-buttons"
          role="group"
          aria-label="Token Input Buttons"
        >
          {currentToken === "cassette_count" && isSpread && (
            <>
              {!(isEyeWedge || isFalseMargins) &&
                !modifierSelections.multipleShaves &&
                !modifierSelections.multipleExcisions &&
                !modifierSelections.multiplePunches && (
                  <button
                    onClick={() => setTokenValue(currentToken, "1")}
                    className={tokenSelection?.value === "1" ? "current" : ""}
                  >
                    {specimen.name}
                  </button>
                )}
              {!isDIFSelected && (
                <span className="GrossingInputs__spreadPrefix">
                  {specimen.name}1 -{" "}
                </span>
              )}
            </>
          )}
          {typeof currentToken === "string" &&
            currentToken.startsWith("type_cassette_count") &&
            isSpread && (
              <>
                {!(
                  isEyeWedge ||
                  isFalseMargins ||
                  (lastStartingTypeOption && options.length > 1)
                ) && (
                  <button
                    onClick={() =>
                      setTokenValue(currentToken, startingTypeOption)
                    }
                    className={
                      tokenSelection?.value === startingTypeOption
                        ? "current"
                        : ""
                    }
                  >
                    {specimen.name}
                    {startingTypeOption}
                  </button>
                )}
                {
                  // Only show the spread prefix if there are multiple options, or if it's the first type cassette count, which is always "2"
                  (options.length > 1 ||
                    (options.length === 1 && options[0] === "2")) && (
                    <span className="GrossingInputs__spreadPrefix">
                      {specimen.name}
                      {lastStartingTypeOption || startingTypeOption} -{" "}
                    </span>
                  )
                }
              </>
            )}
          {currentToken === "cassette_count" && hasTips && (
            <>
              <button
                onClick={() => setTokenValue(currentToken, "2")}
                className={tokenSelection?.value === "2" ? "current" : ""}
              >
                {specimen.name}2
              </button>
              <span className="GrossingInputs__spreadPrefix">
                {specimen.name}2 -{" "}
              </span>
            </>
          )}
          {typeof currentToken === "string" &&
            currentToken.endsWith("cassette_range") && (
              <>
                <button
                  onClick={() => setTokenValue(currentToken, "")}
                  className={tokenSelection?.value === "" ? "current" : ""}
                >
                  {specimen.name}
                  {tokenSelections[currentToken.replace("_range", "_choice")]
                    ?.value || "1"}
                </button>
                <span className="GrossingInputs__spreadPrefix">
                  {specimen.name}
                  {tokenSelections[currentToken.replace("_range", "_choice")]
                    ?.value || "1"}{" "}
                  -{" "}
                </span>
              </>
            )}
          {!!currentToken &&
            options.map((option: string, i: number) =>
              (currentToken === "cassette_count" && hasTips && i === 0) ||
              // For type cassette count, hide the first option (for all save the first) as well as
              // the last option if it's the last type cassette count
              (typeof currentToken === "string" &&
                currentToken.startsWith("type_cassette_count") &&
                ((i === 0 && currentToken !== "type_cassette_count") ||
                  (i !== options.length - 1 &&
                    lastStartingTypeOption))) ? null : (
                <button
                  key={option}
                  onClick={() => setTokenValue(currentToken, option)}
                  style={
                    token?.token === "slow_mohs_shape" && option === "debulk"
                      ? { marginLeft: "auto" }
                      : {}
                  }
                  className={tokenSelection?.value === option ? "current" : ""}
                >
                  {cassetteToken && specimen.name}
                  {option || <span>&nbsp;&nbsp;&nbsp;</span>}
                </button>
              )
            )}
        </div>
        {currentToken !== undefined &&
          (typeof token?.allowCustom === "function"
            ? token?.allowCustom({
                selections: { modifierSelections, tokenSelections, specimen },
              })
            : token?.allowCustom) &&
          (tokenSelection?.custom ? (
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                selectNextToken();
              }}
              className={classNames("Grossing__custom-input-form", {
                big: allowCustom === "big",
              })}
            >
              <input
                // defaultValue={token?.defaultCustomValue}
                // NOTE: because of the way we're 'controlling' the input, we can't actually
                // ... start with a "defaultValue" as you might intuit as an input attribute
                value={tokenSelection?.value || ""}
                onChange={(ev) => {
                  setTokenValue(currentToken, ev.currentTarget.value, true);
                }}
                // NOTE: we have to blur here to perform precision conversions
                // ... otherwise it interferes with keyboard input
                onBlur={
                  allowCustom === "number" || allowCustom === "integer"
                    ? (ev) => {
                        // If we don't have a number, just return immediately
                        if (Number.isNaN(ev.currentTarget.value)) return;

                        let value = Number(ev.currentTarget.value);

                        // If we are an "integer" and it's less than 1, bump it to 1
                        if (
                          allowCustom === "integer" &&
                          value < (token?.customMin || 1)
                        )
                          value = token?.customMin || 1;

                        // For multiple shaves, set the custom min to the type_count if
                        // it's greater than the customMin
                        if (
                          allowCustom === "integer" &&
                          currentToken === "cassette_count" &&
                          tokenSelections.type_count?.value &&
                          parseInt(tokenSelections.type_count.value) >
                            (token?.customMin || 1) &&
                          (modifierSelections.multipleShaves ||
                            modifierSelections.multipleExcisions ||
                            modifierSelections.multiplePunches) &&
                          value < parseInt(tokenSelections.type_count.value)
                        ) {
                          value = parseInt(tokenSelections.type_count.value);
                        }

                        if (
                          allowCustom === "number" &&
                          token?.customMin !== undefined &&
                          value < token.customMin
                        )
                          value = token.customMin;

                        // Use the "step" attribute to determine a "minimum precision"
                        const minimumPrecision =
                          `${token?.step}`.split(".").at(1)?.length ?? 0;

                        // If it's a "number" (not an integer), determine the number of
                        // ... decimal points from the input. This allows manual entry
                        // ... of "2.45" to be specific, without rounding with toFixed
                        const decimals =
                          `${
                            allowCustom === "number"
                              ? `${value}`
                              : token?.step ?? 1
                          }`
                            .split(".")
                            .at(1)?.length ?? 0;

                        // If we have no decimals, go to 0 fixed (natural numbers)
                        // This will round numbers if we want an integer input
                        const precision = Math.max(decimals, minimumPrecision);

                        setTokenValue(
                          currentToken,
                          value.toFixed(precision),
                          true
                        );
                      }
                    : undefined
                }
                type={
                  allowCustom === "number" || allowCustom === "integer"
                    ? "number"
                    : "text"
                }
                step={
                  token?.step // Use step if it's provided, otherwise defaults
                    ? token.step
                    : allowCustom === "number"
                    ? 0.1
                    : allowCustom === "integer"
                    ? 1
                    : undefined
                }
                min={
                  token?.customMin
                    ? currentToken === "cassette_count" &&
                      tokenSelections.type_count?.value &&
                      parseInt(tokenSelections.type_count.value) >
                        token?.customMin &&
                      (modifierSelections.multipleShaves ||
                        modifierSelections.multipleExcisions ||
                        modifierSelections.multiplePunches)
                      ? parseInt(tokenSelections.type_count.value)
                      : token?.customMin
                    : allowCustom === "number"
                    ? 0.1
                    : undefined
                }
                ref={ref}
              />
            </form>
          ) : (
            !isDIFSelected && (
              <button
                className="Grossing__custom-button"
                onClick={() => handleDefaultValue()}
              >
                ⌨️
              </button>
            )
          ))}
        <button onClick={() => selectNextToken()} className="Selection__next">
          &rarr;
        </button>
      </div>
    );
  }
);

const LabStainLabel = ({ labStain }: { labStain: LabStainList }) => {
  const stain = useAppSelector((state) => state.stains[labStain.stain_id]);

  useEffect(() => {
    if (!stain) {
      const warning = `Could not find StainList #${labStain.stain_id} for LabStainList #${labStain.id}`;
      console.warn(warning);
      // TODO: Log warning to Sentry
    }
  }, [labStain, stain]);

  if (!stain) {
    return null;
  }

  return <>{stain.abbreviation}</>;
};

const GrossModifiers = ({
  tokenSelections,
  modifierSelections,
  toggleModifier,
  labStainSelections,
  toggleLabStain,
  labPanelSelections,
  toggleLabPanel,
  specimen,
  noModifiers,
  hasNailProtocol,
  pasfLabStain,
  hasAlopeciaProtocol,
  alopeciaPanel,
  hasPashReflexProtocol,
  hasPasfReflexProtocol,
  pashLabStain,
}: ReturnType<typeof useGrossingMadLib> & {
  specimen: LISSpecimen;
  noModifiers?: boolean;
  hasNailProtocol?: boolean;
  pasfLabStain?: LabStainList;
  hasAlopeciaProtocol?: boolean;
  alopeciaPanel?: LabPanelList;
  hasPashReflexProtocol?: boolean;
  hasPasfReflexProtocol?: boolean;
  pashLabStain?: LabStainList;
}) => {
  const modifierEntries = Object.entries(grossingModifiers).filter(
    ([_name, modifier]) =>
      !modifier.condition ||
      modifier.condition({ modifierSelections, tokenSelections, specimen })
  );
  const selectedLab = useSelectedLab();

  const pashReflexEnabled = selectedLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = selectedLab?.settings?.pasf_reflex?.active;

  const allLabStains = useAppSelector((state) => state.labStains);
  const labStains = Object.values(allLabStains).filter((ls) => {
    // Always include LabStains which have already been selected.
    // Otherwise the backend will use them but they will be invisible on the frontend until ordered.
    // In the future the backend should deselect these automatically when needed using state machines.
    if (labStainSelections.includes(ls.id)) return true;

    // Filter to selected Lab
    if (ls.lab_id !== selectedLab.id) return false;

    // If this Specimen has the NAIL Protocol or the ALOPECIA then we always show the PAS-F Stain option
    // AZDP specifically gets the PAS-H Stain instead of PAS-F for the Nail Protocol
    if (
      ((hasNailProtocol && !pashReflexEnabled) || hasAlopeciaProtocol) &&
      ls.id === pasfLabStain?.id
    )
      return true;

    // If this Specimen has the PAS-H Reflex Protocol then we always show the PAS-H Stain option
    // AZDP specifically gets the PAS-H Stain instead of PAS-F for the Nail Protocol
    if (
      ((hasNailProtocol && pashReflexEnabled) || hasPashReflexProtocol) &&
      ls.id === pashLabStain?.id
    )
      return true;

    if (
      (pasfReflexEnabled || hasPasfReflexProtocol) &&
      ls.id === pasfLabStain?.id
    )
      return true;

    // Do not return any additional LabStains. Protocol support only.
    return false;
  });

  const allLabPanels = useAppSelector((state) => state.labPanels);
  const labPanels = Object.values(allLabPanels).filter((lp) => {
    // Always include LabPanels which have already been selected.
    // Otherwise the backend will use them but they will be invisible on the frontend until ordered.
    // In the future the backend should deselect these automatically when needed using state machines.
    if (labPanelSelections.includes(lp.id)) return true;

    // Filter to selected Lab
    if (lp.lab_id !== selectedLab.id) return false;

    // If this Specimen has the ALOPECIA Protocol then we always show the Alopecia Panel option
    if (hasAlopeciaProtocol && lp.id === alopeciaPanel?.id) return true;

    // Do not return any additional LabPanels. Protocol support only.
    return false;
  });

  return (
    <div className="Grossing__modifiers-and-lab-stains">
      {!noModifiers && (
        <div className="Grossing__modifiers">
          {modifierEntries.map(([name, modifier]) => (
            <button
              key={name}
              onClick={() => toggleModifier(name)}
              className={modifierSelections[name] ? "selected" : ""}
            >
              <span>{modifierSelections[name] ? <>&#10003;</> : "+"}</span>{" "}
              {modifier.label}
            </button>
          ))}
        </div>
      )}

      {(labStains.length > 0 || labPanels.length > 0) && (
        <div className="Grossing__lab-stains">
          {labStains.map((labStain) => (
            <button
              key={labStain.id}
              onClick={() => toggleLabStain(labStain.id)}
              className={
                labStainSelections.includes(labStain.id) ? "selected" : ""
              }
            >
              <span>
                {labStainSelections.includes(labStain.id) ? <>&#10003;</> : "+"}
              </span>{" "}
              <LabStainLabel labStain={labStain} />
            </button>
          ))}
          {labPanels.map((labPanel) => (
            <button
              key={labPanel.id}
              onClick={() => toggleLabPanel(labPanel.id)}
              className={
                labPanelSelections.includes(labPanel.id) ? "selected" : ""
              }
            >
              <span>
                {labPanelSelections.includes(labPanel.id) ? <>&#10003;</> : "+"}
              </span>{" "}
              {labPanel.name} Panel
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Grossing = ({
  specimen,
  grossingMadLib,
  grossBlockDetails,
  workId,
  pathCase,
  complete,
  cancel,
}: {
  specimen: LISSpecimen;
  grossingMadLib: ReturnType<typeof useGrossingMadLib>;
  grossBlockDetails: Partial<LISBlock>[];
  workId?: number;
  pathCase: LISCase;
  complete?: boolean;
  cancel?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const skipAutomaticBarcodePrinting = useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );
  const showPieceCount = useAppSelector((state) =>
    selectFeatureFlag(state, "lisGrossPieceCount")
  );
  const resetGrossMacroButton = useAppSelector((state) =>
    selectFeatureFlag(state, "resetGrossMacroButton")
  );
  const selectedLabStation = useSelectedLabStation();
  const {
    allTokensFilled,
    cassetteCount,
    gross_description_p,
    custom_value_input,
    getDescriptionString,
    debouncedUpdate,
    isUpdatingSpecimen,
    rawText,
    setRawText,
    modifierSelections,
    tokenSelections,
    setTokenValue,
    toggleTextEditor,
    grossingRawText,
    labStainSelections,
    labPanelSelections,
    resetToDefaultTokens,
  } = grossingMadLib;

  const canSubmit =
    (!!rawText || allTokensFilled) && specimen.lis_state !== "grossed";
  const isSubmitting = useIsLoading(`lis/specimens/${specimen.id}/print`);
  const isSubmittingPieceCount = useIsLoading(
    `lis/specimens/${specimen.id}/printPieceCount`
  );
  const selectedLab = useSelectedLab();

  const pashReflexEnabled = selectedLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = selectedLab?.settings?.pasf_reflex?.active;

  const hasNailProtocol = specimen?.protocols?.includes("nail");
  const pasfLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-F"
    )
  );

  const hasPashReflexProtocol = specimen?.protocols?.includes(
    SpecimenProtocolOverride.protocol.PASH_REFLEX
  );
  const hasPasfReflexProtocol = specimen?.protocols?.includes(
    SpecimenProtocolOverride.protocol.PASF_REFLEX
  );
  const pashLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-H"
    )
  );

  const hasAlopeciaProtocol = specimen?.protocols?.includes("alopecia");
  const alopeciaPanel = useAppSelector((state) =>
    Object.values(state.labPanels).find(
      (lp) =>
        lp.lab_id === selectedLab.id && lp.name.toLowerCase() === "alopecia"
    )
  );

  const { printBlock } = useBarcodePrintingCallbacks();

  const onSubmit = (_: any, updating = false) => {
    if (!specimen.id) return;

    const grossDescription = getDescriptionString();

    if (!grossDescription) {
      Sentry.captureException(
        new Error(
          `Specimen #${specimen.id} could not generate a description string`
        )
      );
    }

    // Queue one last update to make sure our final state gets synced to the server
    debouncedUpdate(
      modifierSelections,
      tokenSelections,
      labStainSelections,
      labPanelSelections,
      grossingRawText,
      getDescriptionString(),
      true
    );
    // Execute the debounced update immediately and wait for the response
    debouncedUpdate.flush()?.then(() => {
      if (updating) {
        if (cancel) cancel();
        return;
      }
      if (!workId) return;
      dispatch(
        showPieceCount
          ? printPieceCountSpecimen({
              id: specimen.id || 0,
              parentLookupLabStationId: selectedLabStation.id,
              requestBody: {
                work_id: workId,
                gross_description: grossDescription,
                blocks: grossBlockDetails,
              },
            })
          : printSpecimen({
              id: specimen.id || 0,
              blockCount: cassetteCount,
              parentLookupLabStationId: selectedLabStation.id,
              requestBody: {
                work_id: workId,
                gross_description: grossDescription,
              },
            })
      ).then((response: LISCase) => {
        if (cancel) cancel();
        const asyncAutoPrint = !response._meta?.cassette_printing_task_id;
        if (!skipAutomaticBarcodePrinting && asyncAutoPrint) {
          const newBlockIds = response._meta?.created?.blocks || [];
          printBlock(newBlockIds, pathCase.id as number);
        }
      });
    });
  };

  const grossModifersProps = {
    ...grossingMadLib,
    specimen,
    hasNailProtocol,
    pasfLabStain,
    hasPashReflexProtocol,
    hasPasfReflexProtocol,
    pashLabStain,
    hasAlopeciaProtocol,
    alopeciaPanel,
  };

  return (
    <>
      <div>
        {specimen ? (
          <div className="Grossing">
            <div className="Grossing__steps">
              <div className="Grossing__description">
                {grossingRawText ? (
                  <>
                    <GrossModifiers {...grossModifersProps} noModifiers />
                    <textarea
                      value={rawText}
                      onChange={(ev) => setRawText(ev.target.value)}
                    ></textarea>
                  </>
                ) : (
                  <>
                    <GrossInputs
                      {...grossingMadLib}
                      specimen={specimen}
                      ref={custom_value_input}
                    />

                    <GrossModifiers {...grossModifersProps} />

                    <GrossDescription
                      {...grossingMadLib}
                      specimen={specimen}
                      ref={gross_description_p}
                    />

                    {hasNailProtocol && !pasfLabStain && !pashReflexEnabled && (
                      <div className="red">
                        <em>
                          NAIL Protocol should order a PAS-F Stain by default,
                          but none could be found for {selectedLab.name}
                        </em>
                      </div>
                    )}

                    {hasNailProtocol && !pashLabStain && pashReflexEnabled && (
                      <div className="red">
                        <em>
                          NAIL Protocol should order a PAS-H Stain by default,
                          but none could be found for {selectedLab.name}
                        </em>
                      </div>
                    )}

                    {hasPashReflexProtocol && !pashLabStain && (
                      <div className="red">
                        <em>
                          PAS-H Reflex Protocol should order a PAS-H Stain by
                          default, but none could be found for{" "}
                          {selectedLab.name}
                        </em>
                      </div>
                    )}

                    {hasPasfReflexProtocol && !pasfLabStain && (
                      <div className="red">
                        <em>
                          PAS-F Reflex Protocol should order a PAS-F Stain by
                          default, but none could be found for{" "}
                          {selectedLab.name}
                        </em>
                      </div>
                    )}

                    {hasAlopeciaProtocol && !alopeciaPanel && (
                      <div className="red">
                        <em>
                          ALOPECIA Protocol should order an Alopecia Panel by
                          default, but none could be found for{" "}
                          {selectedLab.name}
                        </em>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Waiting />
        )}
      </div>

      <footer className="Grossing__footer">
        {!complete ? (
          <div className="PlusMinusButtons">
            {grossingRawText && (
              <button
                className="Button MinusButton"
                onClick={() =>
                  setTokenValue("cassette_count", `${cassetteCount - 1}`)
                }
                disabled={cassetteCount <= 1}
              >
                -
              </button>
            )}
            <button
              className="Button SubmitButton"
              disabled={
                !canSubmit ||
                isUpdatingSpecimen ||
                isSubmitting ||
                isSubmittingPieceCount
              }
              onClick={onSubmit}
            >
              Print {cassetteCount} Cassette{cassetteCount === 1 ? "" : "s"}
            </button>
            {grossingRawText && (
              <button
                className="Button PlusButton"
                onClick={() =>
                  setTokenValue("cassette_count", `${cassetteCount + 1}`)
                }
                disabled={cassetteCount >= 9}
              >
                +
              </button>
            )}
          </div>
        ) : (
          <button
            className="Button SubmitButton"
            onClick={(ev) => onSubmit(ev, true)}
          >
            Save Changes
          </button>
        )}
        {!grossingRawText ? (
          <>
            <button
              className="Grossing__footer__edit Button Button--lightGrey"
              onClick={toggleTextEditor}
            >
              Edit as Text
            </button>
            {resetGrossMacroButton && (
              <button
                className="Grossing__footer__edit Button Button--lightGrey"
                onClick={resetToDefaultTokens}
              >
                Reset All Tokens
              </button>
            )}
          </>
        ) : (
          <button
            className="Grossing__footer__edit Button Button--lightGrey"
            onClick={toggleTextEditor}
          >
            {complete ? "Edit as Segments" : "Cancel Text Editing"}
          </button>
        )}
        {complete && (
          <button
            className="Grossing__footer__cancel Button Button--red"
            onClick={cancel}
          >
            Cancel Editing
          </button>
        )}
      </footer>
    </>
  );
};

export default Grossing;
